import type { ModuleConfig } from "../moduleConfig.types";
import {
  BRANDS,
  MARKETS,
  FILTERS,
  SEGMENTS,
  PERIOD_TYPE,
  PERIOD,
  PRE_PERIOD_TYPE,
  PRE_PERIOD_START_DATE,
  PRE_PERIOD_END_DATE,
  STAT_TEST,
  BASE,
  MODE,
  PERIOD_START_DATE,
  PERIOD_END_DATE,
  VISUALIZATION_TYPE,
  PROFILE_GROUP,
  PROFILE_METRIC,
  PROFILE_RESPONSE,
  TRENDS_METRIC,
  GROUPED_BY,
  QUESTION_OPTIONS,
  METRIC,
  RESPONSE,
  SCORE_TYPE,
  VIEW_ID,
} from "@/constant/constantsThrougoutProject";

/**
 * Hierarchical screen config for the Brand Visibility module.
 *
 * Resolution order (later layers override earlier ones):
 *   module.config
 *   → screenTypes.config
 *   → screenTypes.[name].config
 *   → screenTypes.[name].modes.config
 *   → screenTypes.[name].modes.[name].config
 *   → screenTypes.[name].modes.[name].visualisationType.config
 *   → screenTypes.[name].modes.[name].visualisationType.[name].config
 *
 * Note: Visibility By Segment TREND exposes GROUPED_BY and QUESTION_OPTIONS.
 *       POINT_IN_TIME uses PROFILE_GROUP/PROFILE_METRIC/PROFILE_RESPONSE
 *       directly (no intermediate METRIC/RESPONSE step) across all screen types.
 */

export const visibilityModuleConfig: ModuleConfig = {
  // ── Module-level base config ──────────────────────────────────────────────
  config: {
    type: "MODULE",
    module: "VISIBILITY",
    supportMarkets: true,
    isMarketsFilteredByBrand: false,
    minimumRequiredMarkets: 1,
    maximumAllowedMarkets: 25,
    supportBrands: true,
    isBrandsFilteredByMarket: true,
    supportSecondaryBrands: false,
    supportMode: true,
    defaultMode: "POINT_IN_TIME",
    minimumRequiredBrands: 1,
    supportBase: true,
    defaultBase: "GENPOP",
    supportedBases: ["GENPOP", "FAMILIAR"],
    supportDatePeriod: true,
    supportVisualisationSwitch: true,
    supportedVisualisationTypes: ["BARCODE", "BAR", "DISTRIBUTION", "TABLE"],
    defaultVisualisationType: "BARCODE",
    statTestingConfig: [
      { id: "POP", enabled: true, activeLabel: "Period-P" },
      {
        id: "YOY",
        enabled: true,
        activeLabel: "Year-Y",
        disableWhenDaysExceed: {
          days: 365,
          message:
            "Year-over-Year comparison is only available for Months & Quarters of one year or less.",
        },
        disableForPeriods: {
          periods: ["P_W"],
          message:
            "Year-over-Year comparison is only available for Months & Quarters.",
        },
      },
      { id: "PTP", hidden: true },
    ],
    apiMetricKeys: ["awareness", "familiarity", "adRecall"],
    dateBounds: {
      min: "2022-09-21",
      max: null,
      warningMessage:
        "Data for the previous time period comparison is only available from September 21, 2022.",
    },
    postDateBounds: {
      min: "2022-09-21",
      max: null,
      warningMessage:
        "Data for the previous time period comparison is only available from September 21, 2022.",
    },
  },

  screenTypes: {
    SEGMENT: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_ONLY",
        maximumAllowedMarkets: 1,
        maximumAllowedBrands: 1,
        apiGroupedBy: "SEGMENT",
        apiSendFilters: false,
        // Routes: [TREND, POINT_IN_TIME] — no PRE_POST
        supportedModes: ["TREND", "POINT_IN_TIME"],
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportedVisualisationTypes: ["LINE", "TABLE"],
            defaultVisualisationType: "LINE",
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "P_Y",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              RESPONSE,
              VIEW_ID,
              SCORE_TYPE,
            ],
            supportTrendsMetric: true,
            defaultTrendsMetric: "awareness",
            dateBounds: {
              bySubKey: {
                awareness: { min: null, max: null },
                familiarity: { min: null, max: null },
                adRecall: {
                  min: "2022-09-21",
                  max: null,
                  warningMessage:
                    "Data for the previous time period comparison is only available from September 21, 2022.",
                },
              },
            },
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              METRIC,
              RESPONSE,
              VIEW_ID,
              SCORE_TYPE,
            ],
          },
          visualisationType: {
            BAR: {
              config: {
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
            DISTRIBUTION: {
              config: {
                dateBounds: {
                  min: null,
                  max: null,
                  bySubKey: {
                    visibility_ad_recall_2box: {
                      min: "2022-09-21",
                      max: null,
                      warningMessage:
                        "Data for the previous time period comparison is only available from September 21, 2022.",
                    },
                  },
                },
                supportBase: false,
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  VIEW_ID,
                ],
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
          },
        },
      },
    },

    BRAND: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        maximumAllowedMarkets: 1,
        supportedModes: ["TREND", "POINT_IN_TIME", "PRE_POST"],
        apiGroupedBy: "BRAND",
        apiSendFilters: true,
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportedVisualisationTypes: ["LINE", "TABLE"],
            defaultVisualisationType: "LINE",
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "P_Y",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              RESPONSE,
              VIEW_ID,
              SCORE_TYPE,
            ],
            supportTrendsMetric: true,
            defaultTrendsMetric: "awareness",
            dateBounds: {
              bySubKey: {
                awareness: { min: null, max: null },
                familiarity: { min: null, max: null },
                adRecall: {
                  min: "2022-09-21",
                  max: null,
                  warningMessage:
                    "Data for the previous time period comparison is only available from September 21, 2022.",
                },
              },
            },
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              METRIC,
              RESPONSE,
              VIEW_ID,
              SCORE_TYPE,
            ],
          },
          visualisationType: {
            BAR: {
              config: {
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
            DISTRIBUTION: {
              config: {
                supportBase: false,
                dateBounds: {
                  min: null,
                  max: null,
                  bySubKey: {
                    visibility_ad_recall_2box: {
                      min: "2022-09-21",
                      max: null,
                      warningMessage:
                        "Data for the previous time period comparison is only available from September 21, 2022.",
                    },
                  },
                },
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  VIEW_ID,
                ],
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            supportPostDatePeriod: true,
            supportedVisualisationTypes: ["BAR", "TABLE"],
            defaultVisualisationType: "BAR",
            maximumAllowedBrands: 1,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              PRE_PERIOD_TYPE,
              PRE_PERIOD_START_DATE,
              PRE_PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              RESPONSE,
              VIEW_ID,
              SCORE_TYPE,
            ],
            statTestingConfig: [
              { id: "POP", enabled: true, activeLabel: "Period-P" },
              {
                id: "YOY",
                enabled: false,
                activeLabel: "Year-Y",
                disabledMessage:
                  "Year-over-Year comparison is not available on this screen.",
                fallbackTo: "POP",
              },
              {
                id: "PTP",
                hidden: true,
                enabled: false,
                activeLabel: "Peer-P",
                fallbackTo: "POP",
              },
            ],
          },
        },
      },
    },

    // ── By Market ────────────────────────────────────────────────────────────
    MARKET: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        isMarketsFilteredByBrand: true,
        isBrandsFilteredByMarket: false,
        maximumAllowedBrands: 1,
        apiGroupedBy: "MARKET",
        apiSendFilters: true,
        // Routes: [TREND, POINT_IN_TIME] — no PRE_POST
        supportedModes: ["TREND", "POINT_IN_TIME"],
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportedVisualisationTypes: ["LINE", "TABLE"],
            defaultVisualisationType: "LINE",
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "P_Y",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              RESPONSE,
              VIEW_ID,
              SCORE_TYPE,
            ],
            supportTrendsMetric: true,
            defaultTrendsMetric: "awareness",
            dateBounds: {
              bySubKey: {
                awareness: { min: null, max: null },
                familiarity: { min: null, max: null },
                adRecall: {
                  min: "2022-09-21",
                  max: null,
                  warningMessage:
                    "Data for the previous time period comparison is only available from September 21, 2022.",
                },
              },
            },
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              METRIC,
              RESPONSE,
              VIEW_ID,
              SCORE_TYPE,
            ],
          },
          visualisationType: {
            BAR: {
              config: {
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
            DISTRIBUTION: {
              config: {
                supportBase: false,
                dateBounds: {
                  min: null,
                  max: null,
                  bySubKey: {
                    visibility_ad_recall_2box: {
                      min: "2022-09-21",
                      max: null,
                      warningMessage:
                        "Data for the previous time period comparison is only available from September 21, 2022.",
                    },
                  },
                },
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  VIEW_ID,
                ],
                statTestingConfig: [
                  {
                    id: "POP",
                    enabled: false,
                    activeLabel: "Period-P",
                    disabledMessage:
                      "Period-over-Period comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  {
                    id: "YOY",
                    enabled: false,
                    activeLabel: "Year-Y",
                    disabledMessage:
                      "Year-over-Year comparison is not available on this screen.",
                    fallbackTo: "PTP",
                  },
                  { id: "PTP", enabled: true, activeLabel: "Peer-P" },
                ],
              },
            },
          },
        },
      },
    },
  },
};
